// Wire-shape responses for /groups* endpoints.
// Mirrors apps/api/src/modules/identity/groups/route-schemas.ts at the
// zod layer so client + server share one source of truth.
import { z } from 'zod';
import { Group, GroupApplication, GroupInvite, GroupInviteEmailRule, GroupMemberCandidate, } from './group.js';
import { GroupMembership, GroupRole } from './membership.js';
import { GroupVisibility } from './visibility.js';
// ---- group CRUD ----
export const GroupResponse = z.object({ group: Group });
export const GroupListResponse = z.object({
    groups: z.array(Group),
    /** Total count of viewer-visible groups before pagination. Optional so
     *  pre-pagination consumers don't break. */
    total: z.number().int().nonnegative().optional(),
    limit: z.number().int().positive().optional(),
    offset: z.number().int().nonnegative().optional(),
});
export const GroupCapabilitiesResponse = z.object({
    tagsEnabled: z.boolean(),
    hierarchyEnabled: z.boolean(),
});
export const ListGroupsQuery = z.object({
    filter: z.enum(['active', 'archived', 'all']).optional(),
    q: z.string().max(120).optional(),
    tag: z.string().min(1).max(64).optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: z.number().int().min(0).optional(),
});
export const CreateGroupRequest = z.object({
    slug: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9](-?[a-z0-9])*$/, 'slug must be lowercase letters/digits with single hyphens'),
    name: z.string().min(1).max(120),
    description: z.string().max(4000).optional(),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'color must be a #rrggbb hex value')
        .optional(),
    icon: z.string().min(1).max(64).optional(),
    visibility: GroupVisibility.optional(),
    welcomeNote: z.string().max(1000).nullable().optional(),
    parentGroupId: z.string().uuid().nullable().optional(),
    tags: z.array(z.string().min(1).max(64)).max(32).optional(),
});
export const UpdateGroupRequest = CreateGroupRequest.partial().omit({ slug: true });
// ---- house rules ----
export const GroupRuleRow = z.object({
    groupId: z.string().uuid(),
    position: z.number().int().min(0),
    text: z.string().min(1).max(280),
});
export const RulesResponse = z.object({ rules: z.array(GroupRuleRow) });
export const ReplaceRulesRequest = z.object({
    rules: z
        .array(z.object({ position: z.number().int().min(0).max(999), text: z.string().min(1).max(280) }))
        .max(64),
});
// ---- members ----
export const MembersListResponse = z.object({ members: z.array(GroupMembership) });
export const MemberResponse = z.object({ member: GroupMembership });
export const AddMemberRequest = z.object({
    userId: z.string().uuid(),
    role: GroupRole.optional(),
});
export const GroupMemberCandidateListResponse = z.object({
    users: z.array(GroupMemberCandidate),
});
export const ChangeRoleRequest = z.object({ role: GroupRole });
// ---- invites ----
export const InviteRow = GroupInvite;
export const InvitesListResponse = z.object({ invites: z.array(InviteRow) });
export const IssueInviteRequest = z.object({
    targetRole: GroupRole.optional(),
    ttlMinutes: z.number().int().min(1).max(60 * 24 * 90).optional(),
    maxUses: z.number().int().min(1).max(1000).nullable().optional(),
});
export const IssueInviteResponse = z.object({
    invite: InviteRow,
    rawToken: z.string(),
});
export const AddMemberInviteResponse = z.object({
    invite: InviteRow,
});
export const GroupInviteEmailRuleRow = GroupInviteEmailRule;
export const GroupInviteEmailRulesResponse = z.object({
    rules: z.array(GroupInviteEmailRuleRow),
});
export const UpsertInviteEmailRuleRequest = z.object({
    emailSuffix: z.string().min(3).max(255),
    targetRole: GroupRole.optional(),
    enabled: z.boolean().optional(),
});
export const UpdateInviteEmailRuleRequest = z.object({
    emailSuffix: z.string().min(3).max(255).optional(),
    targetRole: GroupRole.optional(),
    enabled: z.boolean().optional(),
});
export const InviteEmailRuleRunResponse = z.object({
    rule: GroupInviteEmailRuleRow,
    invitedCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
});
/** Preview of a tokenised invite — fetched without auth so the recipient
 *  can decide whether to sign in and accept. Mirrors the
 *  `InvitePreviewResponse` TypeBox schema on the API side. */
export const InviteViewerStatus = z.enum(['guest', 'not-member', 'already-member']);
export const InvitePreviewResponse = z.object({
    groupId: z.string().uuid(),
    groupSlug: z.string(),
    groupName: z.string(),
    targetRole: GroupRole,
    expiresAt: z.string().datetime({ offset: true }),
    maxUses: z.number().int().nullable(),
    usedCount: z.number().int(),
    viewerStatus: InviteViewerStatus,
});
export const TargetedInvitePreviewResponse = InvitePreviewResponse.extend({
    inviteId: z.string().uuid(),
});
export const InviteAcceptResponse = z.object({
    member: GroupMembership,
    acceptStatus: z.enum(['joined', 'already-member']),
});
// ---- applications ----
export const ApplicationsListResponse = z.object({
    applications: z.array(GroupApplication),
});
export const ApplicationResponse = z.object({ application: GroupApplication });
export const SubmitApplicationRequest = z.object({
    message: z.string().max(1000).nullable().optional(),
});
// ---- generic ----
export const OkResponse = z.object({ ok: z.literal(true) });
//# sourceMappingURL=responses.js.map